import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs.tsx";
import { contactQueries } from "@/hooks/useContacts.tsx";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/http.ts";
import { Upload } from "lucide-react";
import { useCallback, useState } from "react";

interface AddContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ContactFormData {
  name: string;
  handleValue: string;
  handleType: "emailAddress" | "phoneNumber";
}

/**
 * Parse a vCard file and extract contact information
 */
const parseVCard = (vcardText: string): Partial<ContactFormData> | null => {
  try {
    const lines = vcardText.split(/\r?\n/);
    let name = "";
    let handleValue = "";
    let handleType: "emailAddress" | "phoneNumber" = "emailAddress";

    for (const line of lines) {
      const trimmedLine = line.trim();

      // Parse formatted name (FN)
      if (trimmedLine.startsWith("FN:")) {
        name = trimmedLine.substring(3).trim();
      }

      // Parse name (N) as fallback
      if (!name && trimmedLine.startsWith("N:")) {
        const parts = trimmedLine.substring(2).split(";");
        // Format: LastName;FirstName;MiddleName;Prefix;Suffix
        const lastName = parts[0]?.trim() || "";
        const firstName = parts[1]?.trim() || "";
        name = [firstName, lastName].filter(Boolean).join(" ");
      }

      // Parse email
      if (trimmedLine.startsWith("EMAIL")) {
        const colonIndex = trimmedLine.indexOf(":");
        if (colonIndex !== -1) {
          handleValue = trimmedLine.substring(colonIndex + 1).trim();
          handleType = "emailAddress";
        }
      }

      // Parse phone number
      if (!handleValue && trimmedLine.startsWith("TEL")) {
        const colonIndex = trimmedLine.indexOf(":");
        if (colonIndex !== -1) {
          handleValue = trimmedLine.substring(colonIndex + 1).trim();
          handleType = "phoneNumber";
        }
      }
    }

    if (name || handleValue) {
      return { name, handleValue, handleType };
    }

    return null;
  } catch (error) {
    console.error("Failed to parse vCard:", error);
    return null;
  }
};

/**
 * Dialog for adding a new contact either manually or via vCard upload
 */
export const AddContactDialog = ({ open, onOpenChange }: AddContactDialogProps) => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    handleValue: "",
    handleType: "emailAddress",
  });
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const queryClient = useQueryClient();

  const createContactMutation = useMutation({
    mutationFn: async (data: { contactName: string; handleType: string; handleValue: string }) => {
      const response = await api.post("/api/a/contacts/handles/new", data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contactQueries.all() });
    },
  });

  const resetForm = () => {
    setFormData({ name: "", handleValue: "", handleType: "emailAddress" });
    setError(null);
  };

  const handleClose = () => {
    if (!createContactMutation.isPending) {
      resetForm();
      onOpenChange(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.handleValue.trim()) {
      setError("Name and contact information are required");
      return;
    }

    setError(null);

    try {
      await createContactMutation.mutateAsync({
        contactName: formData.name,
        handleType: formData.handleType,
        handleValue: formData.handleValue,
      });

      resetForm();
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to create contact:", error);
      setError("Failed to create contact. Please try again.");
    }
  };

  const handleFileUpload = useCallback(async (file: File) => {
    setError(null);

    if (!file.name.endsWith(".vcf") && !file.name.endsWith(".vcard")) {
      setError("Please upload a valid vCard file (.vcf or .vcard)");
      return;
    }

    try {
      const text = await file.text();
      const parsed = parseVCard(text);

      if (!parsed) {
        setError("Could not parse vCard file. Please check the file format.");
        return;
      }

      setFormData((prev) => ({
        name: parsed.name || prev.name,
        handleValue: parsed.handleValue || prev.handleValue,
        handleType: parsed.handleType || prev.handleType,
      }));
    } catch (error) {
      console.error("Failed to read vCard file:", error);
      setError("Failed to read vCard file. Please try again.");
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFileUpload(files[0]);
      }
    },
    [handleFileUpload],
  );

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add New Contact</DialogTitle>
          <DialogDescription>
            Create a new contact by entering details manually or uploading a vCard file.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="manual" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="manual">Manual Entry</TabsTrigger>
            <TabsTrigger value="vcard">Upload vCard</TabsTrigger>
          </TabsList>

          <TabsContent value="manual" className="space-y-4 mt-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="handleType">Contact Type *</Label>
                <Select
                  value={formData.handleType}
                  onValueChange={(value: "emailAddress" | "phoneNumber") =>
                    setFormData((prev) => ({ ...prev, handleType: value }))
                  }
                >
                  <SelectTrigger id="handleType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="emailAddress">Email Address</SelectItem>
                    <SelectItem value="phoneNumber">Phone Number</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="handleValue">
                  {formData.handleType === "emailAddress" ? "Email Address" : "Phone Number"} *
                </Label>
                <Input
                  id="handleValue"
                  type={formData.handleType === "emailAddress" ? "email" : "tel"}
                  placeholder={
                    formData.handleType === "emailAddress" ? "john@example.com" : "+1 234 567 8900"
                  }
                  value={formData.handleValue}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, handleValue: e.target.value }))
                  }
                  required
                />
              </div>

              {error && <div className="text-sm text-destructive">{error}</div>}

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={createContactMutation.isPending}
                  data-statsig-name="contact-add-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createContactMutation.isPending}
                  data-statsig-name="contact-add-submit"
                >
                  {createContactMutation.isPending ? "Adding..." : "Add Contact"}
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>

          <TabsContent value="vcard" className="space-y-4 mt-4">
            <div
              className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragging ? "border-primary bg-primary/5" : "border-muted"
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <div className="space-y-2">
                <p className="text-sm font-medium">Drop vCard file here</p>
                <p className="text-xs text-muted-foreground">or click to browse</p>
              </div>
              <input
                type="file"
                accept=".vcf,.vcard"
                onChange={handleFileInputChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
            </div>

            {(formData.name || formData.handleValue) && (
              <div className="space-y-4">
                <div className="rounded-lg border p-4 space-y-3">
                  <h4 className="text-sm font-medium">Parsed Contact Information:</h4>
                  {formData.name && (
                    <div className="text-sm">
                      <span className="text-muted-foreground">Name:</span>
                      <span className="font-medium">{formData.name}</span>
                    </div>
                  )}
                  {formData.handleValue && (
                    <div className="text-sm">
                      <span className="text-muted-foreground">
                        {formData.handleType === "emailAddress" ? "Email: " : "Phone: "}
                      </span>
                      <span className="font-medium">{formData.handleValue}</span>
                    </div>
                  )}
                </div>

                {error && <div className="text-sm text-destructive">{error}</div>}

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={createContactMutation.isPending}
                    data-statsig-name="contact-add-vcard-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={
                      createContactMutation.isPending || !formData.name || !formData.handleValue
                    }
                    data-statsig-name="contact-add-vcard-submit"
                  >
                    {createContactMutation.isPending ? "Adding..." : "Add Contact"}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
